import { useMemo, useState } from "react";
import { resources } from "@constants/hardcoded-strings";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import {
    AXNUILayout,
    AXNUITheme,
    AXNUIThemeGeneral
} from "@iddataweb/idw-common-ui/types";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { ThemingFormContainer } from "../../../../common/theming-form-container/theming-form-container";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { ThemingLayoutGeneralSection } from "./theming-layout-general-section/theming-layout-general-section";
import { ThemingLayoutStyleSection } from "./theming-layout-style-section/theming-layout-style-section";
import { ThemingLayoutBackgroundSection } from "./theming-layout-background-section/theming-layout-background-section";
import { ThemingConfigurationContainer } from "../../../../common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "../../../../common/theming-sub-header-container/theming-sub-header-container";
import { PageLevelTypes } from "@enums/page-level-types";
import { ThemingLayoutLanguageSelectorSection } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-layout-tab-container/theming-layout-language-selector-section/theming-layout-language-selector-section";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";

type ThemingLayoutTabContainerProps = {
    isDisabled: boolean;
    axnuiLayout: AXNUILayout;
    axnuiTheme: AXNUITheme;
    inheritedAXNUILayout: AXNUILayout;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    customLanguages: Array<LocaleTypes>;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    updateAXNUILayoutPropertyValue: (axnuiLayout: AXNUILayout) => void;
    updateAXNUIThemePropertyValue: (axnuiTheme: AXNUITheme) => void;
};

export const ThemingLayoutTabContainer = (props: ThemingLayoutTabContainerProps) => {
    const {
        isDisabled,
        axnuiLayout,
        axnuiTheme,
        inheritedAXNUILayout,
        inheritedAXNUITheme,
        pageLevel,
        customLanguages,
        generatePreviewComponent,
        uploadFileToS3,
        updateAXNUILayoutPropertyValue,
        updateAXNUIThemePropertyValue
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateAXNUIThemeGeneralFieldValue = (key: keyof AXNUIThemeGeneral, value: string) => {
        const newPropertyValue: AXNUITheme = {
            ...axnuiTheme,
            general: {
                ...axnuiTheme.general,
                [key]: value
            }
        };
        updateAXNUIThemePropertyValue(newPropertyValue);
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.LAYOUT);
    }, [generatePreviewComponent]);




    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.general.title} showSeparator={true}>
                            <ThemingLayoutGeneralSection
                                labelWidth={labelWidth}
                                layout={layout}
                                isDisabled={isDisabled}
                                axnuiLayout={axnuiLayout}
                                axnuiTheme={axnuiTheme}
                                inheritedAXNUILayout={inheritedAXNUILayout}
                                inheritedAXNUITheme={inheritedAXNUITheme}
                                pageLevel={pageLevel}
                                uploadFileToS3={uploadFileToS3}
                                updateAXNUIThemeGeneralFieldValue={updateAXNUIThemeGeneralFieldValue}
                                updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                            />
                        </ThemingFormSection>
                        <ThemingFormSection title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.title} showSeparator={true}>
                            <ThemingLayoutStyleSection
                                labelWidth={labelWidth}
                                layout={layout}
                                isDisabled={isDisabled}
                                axnuiTheme={axnuiTheme}
                                inheritedAXNUITheme={inheritedAXNUITheme}
                                pageLevel={pageLevel}
                                updateAXNUIThemeGeneralFieldValue={updateAXNUIThemeGeneralFieldValue}
                            />
                        </ThemingFormSection>
                        <ThemingFormSection title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.title}>
                            <ThemingLayoutBackgroundSection
                                labelWidth={labelWidth}
                                layout={layout}
                                isDisabled={isDisabled}
                                axnuiTheme={axnuiTheme}
                                inheritedAXNUITheme={inheritedAXNUITheme}
                                pageLevel={pageLevel}
                                uploadFileToS3={uploadFileToS3}
                                updateAXNUIThemeGeneralFieldValue={updateAXNUIThemeGeneralFieldValue}
                            />
                        </ThemingFormSection>

                        {customLanguages.length > 1 && (
                            <ThemingFormSection title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.languageSelector.title}>
                            <ThemingLayoutLanguageSelectorSection
                                labelWidth={labelWidth}
                                layout={layout}
                                isDisabled={isDisabled}
                                axnuiLayout={axnuiLayout}
                                inheritedAXNUILayout={inheritedAXNUILayout}
                                pageLevel={pageLevel}
                                updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}

                            />
                        </ThemingFormSection>
                        )}
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};